// FILE: client/src/screens/admin/categories/CreateCategoryModal.jsx

import React, { useEffect, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import { adminCreateCategory } from "../../actions/categoryActions";
import Uploader from "../../components/images/Uploader";
import { isValidCatalogKey } from "../../constants/shopMeta";
import { getApiErrorMessage } from "../../utils/errorMessages";

const getErrorMessage = (error) =>
  getApiErrorMessage(error, {
    fallback:
      "We could not create this category right now. Please try again later.",
  });

const CreateCategoryModal = ({ show, onHide, shopKey, onCreated }) => {
  const dispatch = useDispatch();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [isPublished, setIsPublished] = useState(true);
  const [images, setImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!show) {
      setName("");
      setDescription("");
      setIsPublished(true);
      setImages([]);
      setSubmitting(false);
      setError("");
    }
  }, [show]);

  const handleClose = () => {
    if (submitting) return;
    onHide?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const safeShopKey = String(shopKey || "").trim();
    const safeName = String(name || "").trim();

    if (!isValidCatalogKey(safeShopKey)) {
      setError("Please select a valid catalogue before creating a category.");
      return;
    }

    if (!safeName) {
      setError("Category name is required.");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const created = await dispatch(
        adminCreateCategory({
          shopKey: safeShopKey,
          name: safeName,
          slug: "",
          description: String(description || "").trim(),
          isPublished,
          images,
        }),
      );

      toast.success("Category created.");
      onCreated?.(created);
      onHide?.();
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      backdrop="static"
    >
      <Form
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <Modal.Header closeButton={!submitting}>
          <Modal.Title>New Category</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error ? <Alert variant="danger">{error}</Alert> : null}

          <Form.Group className="mb-3" controlId="categoryName">
            <Form.Label>Name</Form.Label>

            <Form.Control
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              autoFocus
              disabled={submitting}
              placeholder="Enter category name"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="categoryDescription">
            <Form.Label>Description</Form.Label>

            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              disabled={submitting}
              placeholder="Optional category description"
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="categoryPublished">
            <Form.Check
              type="checkbox"
              label="Published"
              checked={isPublished}
              onChange={(event) => setIsPublished(event.target.checked)}
              disabled={submitting}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Category images</Form.Label>

            <Uploader
              existingImages={images}
              setImageUrls={setImages}
              folder={`categories/${shopKey || "uncategorized"}`}
            />

            <Form.Text className="text-muted">
              Upload images first, then create the category.
            </Form.Text>
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="button"
            variant="outline-secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>

          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              "Create category"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default CreateCategoryModal;
